import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useUser } from './UserContext';
import { useMachine } from './context/MachineContext';
import { useEmployee } from './EmployeeContext';
import { useTheme } from '../theme/ThemeContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const TIME_ZONE = 'Asia/Colombo';

export default function LiveClock() {
  const { username } = useUser();
  const { machineCode } = useMachine();
  const { employeeName, setEmployeeName } = useEmployee();
  const { mode, setMode, isDark } = useTheme();

  const [machineDescription, setMachineDescription] = useState('');
  const [open, setOpen] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [is24h, setIs24h] = useState(() => localStorage.getItem('liveclock-24h') === '1');

  // clock
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // fetch employee
  useEffect(() => {
    if (!username) return;
    axios.get(`${API_BASE_URL}/UserName?EmpID=${encodeURIComponent(username)}`)
      .then(res => setEmployeeName(res.data?.Name || ''))
      .catch(() => setEmployeeName(''));
  }, [username, setEmployeeName]);

  // fetch machine
  useEffect(() => {
    if (!machineCode) return;
    axios.get(`${API_BASE_URL}/MachineName?machineCode=${encodeURIComponent(machineCode)}`)
      .then(res => setMachineDescription(res.data?.MachineDescription || ''))
      .catch(() => setMachineDescription(''));
  }, [machineCode]);

  const clockText = useMemo(() => {
    const timeStr = now.toLocaleTimeString([], {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: !is24h, timeZone: TIME_ZONE,
    });
    const dateStr = now.toLocaleDateString([], {
      weekday: 'short', year: 'numeric', month: 'short', day: '2-digit', timeZone: TIME_ZONE,
    });
    return { timeStr, dateStr, seconds: now.getSeconds() };
  }, [now, is24h]);

  const toggleInfo = () => setOpen(p => !p);
  const toggleFormat = () => {
    setIs24h(p => {
      const next = !p;
      localStorage.setItem('liveclock-24h', next ? '1' : '0');
      return next;
    });
  };
  const cycleTheme = () => setMode(mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system');

  // auto-hide
  useEffect(() => {
    if (!open || hovering) return;
    const t = setTimeout(() => setOpen(false), 6000);
    return () => clearTimeout(t);
  }, [open, hovering, clockText.seconds]);

  // tokens from CSS vars
  const panelBg = 'var(--bg-panel)';
  const borderCol = 'var(--border-subtle)';
  const shadow = 'var(--shadow-elev)';
  const textCol = 'var(--text-primary)';
  const subCol = 'var(--text-subtle)';
  const themeLabel = mode === 'system' ? 'Theme: System' : mode === 'light' ? 'Theme: Light' : 'Theme: Dark';

  if (!machineCode && !username) return null;

  return (
    <>
      {/* FAB */}
      <div style={{ position: 'fixed', bottom: 20, right: 24, zIndex: 1300 }}>
        <button
          aria-label="Open live clock and user/machine info"
          onClick={toggleInfo}
          onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateZ(0) scale(1.06)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateZ(0) scale(1)')}
          style={{
            height: 56, width: 56, borderRadius: 9999,
            background: 'linear-gradient(135deg, var(--accent-1), var(--accent-2))',
            color: '#fff', border: 'none',
            boxShadow: '0 10px 24px rgba(99,102,241,0.35)',
            cursor: 'pointer', display: 'grid', placeItems: 'center',
            transition: 'transform 160ms ease, box-shadow 160ms ease',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </button>
      </div>

      {/* Panel */}
      <div
        role="dialog" aria-modal="false" aria-label="Live clock panel"
        onMouseEnter={() => setHovering(true)} onMouseLeave={() => setHovering(false)}
        style={{
          position: 'fixed', top: 88, left: '50%', width: 380, maxWidth: '92vw',
          borderRadius: 16, backdropFilter: 'blur(10px)',
          backgroundColor: panelBg, border: `1px solid ${borderCol}`, boxShadow: shadow,
          zIndex: 1299, opacity: open ? 1 : 0,
          transform: open ? 'translate(-50%, 0)' : 'translate(-50%, -110%)',
          transition: 'transform 260ms cubic-bezier(.2,.8,.2,1), opacity 220ms ease',
          pointerEvents: open ? 'auto' : 'none',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '12px 14px',
          borderTopLeftRadius: 16, borderTopRightRadius: 16,
          background: isDark
            ? 'linear-gradient(135deg, rgba(99,102,241,0.25), rgba(139,92,246,0.25))'
            : 'linear-gradient(135deg, rgba(99,102,241,0.15), rgba(139,92,246,0.15))',
          borderBottom: `1px solid ${borderCol}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                 stroke={subCol} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <div style={{ lineHeight: 1.1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: textCol }} aria-live="polite">
                {clockText.timeStr}
                <span style={{ fontWeight: 500, fontSize: 12, color: subCol }}> ({TIME_ZONE})</span>
              </div>
              <div style={{ fontSize: 12, color: subCol, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {clockText.dateStr}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconButton
              label={`${themeLabel} (click to change)`}
              onClick={cycleTheme}
              icon={
                mode === 'light' ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                       stroke={subCol} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                  </svg>
                ) : mode === 'dark' ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                       stroke={subCol} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                       stroke={subCol} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3v3M12 18v3M4.6 5l2.1 2.1M17.3 17.3l2.1 2.1M3 12h3M18 12h3M4.6 19l2.1-2.1M17.3 6.7l2.1-2.1" />
                    <path d="M12 8a4 4 0 0 0 0 8V8z" />
                  </svg>
                )
              }
            />
            <IconButton
              label={is24h ? 'Switch to 12-hour' : 'Switch to 24-hour'}
              onClick={toggleFormat}
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                     stroke={subCol} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
                  <path d="M7 12h10" />
                </svg>
              }
            />
            <IconButton
              label="Close"
              onClick={() => setOpen(false)}
              icon={
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                     stroke={subCol} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              }
            />
          </div>
        </div>

        {/* Progress */}
        <div aria-hidden="true" style={{
          height: 2,
          width: `${(clockText.seconds / 60) * 100}%`,
          background: 'linear-gradient(90deg, var(--accent-1), var(--accent-2))',
          transition: 'width 600ms linear',
        }} />

        {/* Body */}
        <div style={{ padding: 14 }}>
          {machineCode && (
            <InfoRow label="Machine" value={`${machineCode} — ${machineDescription || '…'}`} />
          )}
          {username && (
            <InfoRow label="Employee" value={employeeName || username} />
          )}
          <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-subtle)' }}>
            {themeLabel}{mode === 'system' ? (isDark ? ' (System = Dark)' : ' (System = Light)') : ''}
          </div>
        </div>
      </div>
    </>
  );
}

function IconButton({ label, onClick, icon }) {
  return (
    <button
      type="button" onClick={onClick} title={label} aria-label={label}
      style={{
        height: 30, width: 30, borderRadius: 8, display: 'grid', placeItems: 'center',
        background: 'transparent', border: '1px solid rgba(0,0,0,0.06)',
        cursor: 'pointer', transition: 'background 160ms ease, transform 160ms ease',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.06)')}
      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
    >
      {icon}
    </button>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: 12, padding: '10px 12px', borderRadius: 12,
      background: 'var(--row-bg)'
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-subtle)'
        }}>{label}</div>
        <div style={{
          fontSize: 14, fontWeight: 600, color: 'var(--text-primary)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
        }}>{value}</div>
      </div>
    </div>
  );
}
